import { FC, useEffect, useRef, useState } from "react";
import {
  EdgeProps,
  EdgeLabelRenderer,
  BaseEdge,
  useStore,
} from "reactflow";
import { drag } from "d3-drag";
import { select } from "d3-selection";
import { CustomEdgeContainer } from "./styled";

// Utility function for edge center calculation (copied from reactflow lib)
function getEdgeCenter({
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}) {
  const xOffset = Math.abs(targetX - sourceX) / 2;
  const centerX = targetX < sourceX ? targetX + xOffset : targetX - xOffset;

  const yOffset = Math.abs(targetY - sourceY) / 2;
  const centerY = targetY < sourceY ? targetY + yOffset : targetY - yOffset;

  return { centerX, centerY, xOffset, yOffset };
}

// Store for edge label Y positions
let storeYVal: Record<string, number> = {};
let zoom = 0;

const CustomEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd
}) => {
  const { centerX, centerY } = getEdgeCenter({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });
  const increment = useRef(0)
  const [labelPointY, setLabelPointY] = useState(storeYVal?.[id] || 0);

  // Get the zoom level from ReactFlow store
  useStore((state) => {
    zoom = state.transform[2];
  });

  const edgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (edgeRef.current) {
      const d3Selection: any =
        select(edgeRef.current);

      const dragBehavior: any = drag<HTMLDivElement, unknown>()
        .on("drag", (event) => {
          setLabelPointY((prev) => prev - event.dy / zoom);
        });

      d3Selection.call(dragBehavior);
    }
  }, [edgeRef, zoom]);

  const getCalculatedPosition = (centerIndex: number, gap: number) => {
    if (data.sourceIndex === data.targetIndex - 1) {
      increment.current = (data.currentOption - centerIndex) * (gap + 10);
    } else if (Math.abs(data.targetIndex - data.sourceIndex) < 3) {
      increment.current = (data.currentOption - centerIndex - (data.totalOptionCount / 2 < data.currentOption ? -4 : 4)) * gap;
    } else if (Math.abs(data.targetIndex - data.sourceIndex) < 6) {
      increment.current = (data.currentOption - centerIndex - (data.totalOptionCount / 2 < data.currentOption ? -6 : 6)) * gap;
    } else if (Math.abs(data.targetIndex - data.sourceIndex) < 9) {
      increment.current = (data.currentOption - centerIndex - (data.totalOptionCount / 2 < data.currentOption ? -9 : 9)) * gap;
    } else {
      increment.current = (data.currentOption - centerIndex - (data.totalOptionCount / 2 < data.currentOption ? -12 : 12)) * gap;
    }
  }

  // Generate an orthogonal edge path
  function generateOrthogonalEdgePath(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    Hoffset = 0
  ): string {
    const gap = 20; // Define the spacing between options

    if (!data?.totalOptionCount || data?.totalOptionCount === 1) {
      increment.current = 0;
    } else {
      const centerIndex = (data.totalOptionCount + 1) / 2;
      getCalculatedPosition(centerIndex, gap)
    }

    const dy = endY - startY;
    let path = `M${startX},${startY} `;
    path += `H${startX + ((!data?.totalOptionCount || data?.totalOptionCount < 3) ? 20 : Math.abs(increment.current) / 2)} `;
    path += `V${startY + dy / 2 - Hoffset + increment.current} `;
    path += `H${endX + 10 - ((!data?.totalOptionCount || data?.totalOptionCount < 3) ? 20 : Math.abs(increment.current) / 2)} `;
    path += `V${endY}`;
    path += `H${endX}`;

    return path;
  }

  // Generate edge path
  const edgePath = generateOrthogonalEdgePath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    labelPointY
  );

  return (
    <>
      {/* Render the edge */}
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} />
      {/* Render the draggable label */}
      <EdgeLabelRenderer>
        <div
          ref={edgeRef}
          style={{
            position: "absolute",
            left: `${centerX - 95}px`,
            top: `${centerY - 15 - labelPointY + (increment?.current || 0)}px`,
            zIndex: 9999,
            opacity: 1,
            width: "200px",
            height: "30px",
            pointerEvents: "all",
            cursor: "pointer",
          }}
        />
        <CustomEdgeContainer
          labelX={centerX}
          labelY={centerY - labelPointY + (increment?.current || 0)}
          className="nodrag nopan"
        >
          {data?.label?.length > 30
            ? `${data?.label?.slice(0, 35)}...`
            : data?.label}
        </CustomEdgeContainer>
      </EdgeLabelRenderer>
    </>
  );
};

export default CustomEdge;